import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import GetCookie from "../../../../utils/GetCookie";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import DeleteConfirmationModal from "../../../../utils/DeleteConfirmationModal";
import ConfirmationModal from "../../../../utils/ConfirmationModal";
import authNavigate from "../../../../globalComponents/AuthNav";
import DocumentPreview from "../../../../utils/DocumentPreview";
import FormTeamsList from "../../../dataFabrics/FormTeamsList";

const SOURCE_EXTENSIONS = {
  PUBMED: [".txt", ".nbib", ".csv"],
  EMBASE: [".ris", ".csv", ".xlsx"],
  COCHRANE: [".txt", ".ris", ".csv"],
};

// Mirrors SourceFormat in slr_file_upload_parser.py. Shown after validation so
// the user can confirm they downloaded the export they meant to -- the
// commonest mistake is grabbing Embase's "Fields by: Row" layout instead of by
// column.
const FORMAT_LABELS = {
  MEDLINE_TAGGED: "a PubMed export",
  PUBMED_CSV_TEMPLATE: "the CSV template",
  EMBASE_TABULAR_COLUMN: "an Embase spreadsheet export",
  EMBASE_RIS: "an Embase RIS export",

  COCHRANE_PLAIN_TEXT: "a Cochrane plain text export",
  COCHRANE_RIS: "a Cochrane RIS export",
  COCHRANE_CSV: "a Cochrane CSV export",
};

// Shown under the file picker, following the Source database toggle. Only the
// steps for the database the user actually picked are on screen, so the one
// thing they can get wrong -- Embase's Row layout, which may be their saved
// default -- is not buried next to instructions for the other database.
const SOURCE_GUIDANCE = {
  PUBMED: {
    label: "PubMed",
    steps: (
      <>
        Upload either a <b>.txt</b> (Save → Format: <b>PubMed</b>) or <b>.nbib</b> file
        (Send to → <b>Citation manager</b>) or upload a CSV with the fields written below
      </>
    ),
  },
  EMBASE: {
    label: "Embase",
    steps: (
      <>
        Formats supported: <b>RIS</b>, <b>CSV</b>, or <b>MS Excel</b>. Spreadsheets
        must use <b>Fields by: Column</b> while exporting from Embase
      </>
    ),
  },

   COCHRANE: {
    label: "Cochrane",
    steps: (
      <>
        Formats supported: <b>Plain Text (.txt)</b>, <b>RIS (.ris)</b>, or{" "}
        <b>CSV (.csv)</b>. Export the references directly from Cochrane
        and upload the downloaded file.
      </>
    ),
  },
};

// `open`/`onToggle` make this controllable by a parent (used to keep the
// Project Team and Full-Text Screening Documents sections mutually exclusive
// below — only one of them can be open at a time). When neither is passed,
// Section falls back to managing its own open/closed state exactly as before,
// so any other, unrelated usage of it keeps working unchanged.
const Section = ({ title, defaultOpen = false, open: openProp, onToggle, children }) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const toggle = () => (isControlled ? onToggle?.() : setInternalOpen((o) => !o));

  return (
    <div className="discovery-section mb-2">
      <button
        type="button"
        className="discovery-section-header"
        onClick={toggle}
      >
        <span className="ev-small-p-dark ev-bold-med text-truncate">{title}</span>
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11 flex-shrink-0`} />
      </button>
      {open && <div className="discovery-section-body">{children}</div>}
    </div>
  );
};

const pickFabricId = (obj) =>
  obj?.payload?.slr_fabric_id ||
  obj?.payload?.fabric_id ||
  obj?.payload?.project_id ||
  obj?.slr_fabric_id ||
  obj?.fabric_id ||
  obj?.project_id ||
  null;

const GeneralSettingsForm = forwardRef(
  (
    {
      editingProject = null,
      stageData = null,
      onSuccess,
      onDataChange,
      onCancel,
      onBackToProjects,
      onNavigateToDiscovery,
      onOpenScreeningPackage,
    },
    ref,
  ) => {
    const navigate = useNavigate();

    const API_BASE_URL = "/pceapi/slr";
    const headers = useMemo(
      () => ({
        Authorization: GetCookie("access_token"),
      }),
      [],
    );

    const [formData, setFormData] = useState({
      project_name: "",
      project_description: "",
      access_level: "PRIVATE", // PUBLIC | PRIVATE
      team_id: null,
    });

    const abstractFileRef = useRef(null);
    const [abstractCsvFile, setAbstractCsvFile] = useState(null);
    const [mappingChecked, setMappingChecked] = useState(false);
    const [mappingValid, setMappingValid] = useState(false);
    const [mappingMessage, setMappingMessage] = useState("");
    const [mappingFormat, setMappingFormat] = useState("");
    const [mappingWarnings, setMappingWarnings] = useState([]);
    const [mappingSourceMismatch, setMappingSourceMismatch] = useState(false);
    const [missingTitleMessage, setMissingTitleMessage] = useState("");

    // ---- Data Source selector: "discovery" | "csv" | "later" ----
    const [dataSourceMode, setDataSourceMode] = useState("discovery");

    // Import from Discovery
    const [discoveries, setDiscoveries] = useState([]);
    const [discoveriesLoading, setDiscoveriesLoading] = useState(false);
    const [discoveriesLoadingMore, setDiscoveriesLoadingMore] = useState(false);
    const [discoveriesTotal, setDiscoveriesTotal] = useState(0);
    const [discoveriesPage, setDiscoveriesPage] = useState(1);
    const [discoverySearchQuery, setDiscoverySearchQuery] = useState("");
    const [selectedSearchId, setSelectedSearchId] = useState(null);
    // Cached display name for the selected discovery — kept in sync by the
    // effect below, and survives the discoveries list being re-filtered by search.
    const [selectedDiscoveryLabel, setSelectedDiscoveryLabel] = useState(null);
    const [migrationWarning, setMigrationWarning] = useState("");
    const [selectedTeamName, setSelectedTeamName] = useState(null);
    // Team-change warning: holds { teamId, teamName, nonMembers: [{email}] } when
    // reviewers not in the incoming team are detected; null = no pending change.
    const [pendingTeamChange, setPendingTeamChange] = useState(null);
    const [abstractMigration, setAbstractMigration] = useState(null);
    const migrationInFlightRef = useRef(false);
    const [discoveryImports, setDiscoveryImports] = useState([]);

    // Upload CSV — source type hint (affects the sample template only)
    const [csvSourceType, setCsvSourceType] = useState("PUBMED"); // PUBMED | EMBASE | COCHRANE

    // Delete Abstract Data
    const [showDeleteAbstractsModal, setShowDeleteAbstractsModal] = useState(false);
    const [deleteAbstractsConfirmText, setDeleteAbstractsConfirmText] = useState("");
    const [abstractsPreviewOpen, setAbstractsPreviewOpen] = useState(false);
    const [abstractsPreviewLoading, setAbstractsPreviewLoading] = useState(false);
    const [abstractsPreviewLoadingMore, setAbstractsPreviewLoadingMore] = useState(false);
    const [abstractsPreviewList, setAbstractsPreviewList] = useState([]);
    const [abstractsPreviewTotal, setAbstractsPreviewTotal] = useState(0);
    const [abstractsPreviewPage, setAbstractsPreviewPage] = useState(1);
    const [abstractsPreviewSearch, setAbstractsPreviewSearch] = useState("");
    const ABSTRACTS_PREVIEW_PAGE_SIZE = 50;
    const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    // Full-text upload (PDFs)
    const fullTextRef = useRef(null);
    const [fullTextFiles, setFullTextFiles] = useState([]);

    const [documents, setDocuments] = useState([]); // {id, name, url?}
    const [docSearch, setDocSearch] = useState("");
    const [selectedDocIds, setSelectedDocIds] = useState(new Set());
    const [docsTotal, setDocsTotal] = useState(0);
    const [visibleCount, setVisibleCount] = useState(15);

    // Which of the two left-column collapsible sections (Project Team /
    // Full-Text Screening Documents) is open — "team" | "fulltext" | null.
    // Keeping this as a single value (instead of one boolean per section) is
    // what makes them mutually exclusive: opening one necessarily closes the
    // other. Initial value mirrors the old per-section `defaultOpen` props
    // (open whichever one already has data), evaluated once on mount.
    const [openLeftSection, setOpenLeftSection] = useState(
      () => (formData.team_id ? "team" : documents.length > 0 ? "fulltext" : null)
    );

    const [abstractsCount, setAbstractsCount] = useState(0);

    const [createdProjectMeta, setCreatedProjectMeta] = useState(null); // {project_id, s3_path, ...}
    const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
    const [localPreviewName, setLocalPreviewName] = useState(null);
    const [localPreviewLoading, setLocalPreviewLoading] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDocId, setPreviewDocId] = useState(null);
    const [previewDocName, setPreviewDocName] = useState("");

    // Popups
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] =
      useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [pendingDeleteName, setPendingDeleteName] = useState("");

    useEffect(() => {
      if (!editingProject) return;

      setFormData({
        project_name: editingProject.project_name || editingProject.name || "",
        project_description:
          editingProject.project_description ||
          editingProject.description ||
          "",
        access_level:
          editingProject.access_level ||
          editingProject.project_access_level ||
          "PRIVATE",
        team_id: editingProject.team_id || null,
      });

      setMappingChecked(true);
      setMappingValid(true);

      setAbstractsCount(editingProject.abstracts_count || 0);
      if (Array.isArray(editingProject.documents)) {
        setDocuments(editingProject.documents);
        setDocsTotal(editingProject.documents.length);
      }
    }, [editingProject]);

    useEffect(() => {
      if (!stageData) return;

      setFormData((prev) => ({
        ...prev,
        project_name: stageData?.name || prev.project_name || "",
        project_description:
          stageData?.description || prev.project_description || "",
        access_level:
          stageData?.fabric_access_level || prev.access_level || "PRIVATE",
        team_id: stageData?.team_id ?? prev.team_id ?? null,
      }));

      if (stageData?.team_name) setSelectedTeamName(stageData.team_name);

      if (typeof stageData?.abstracts_count === "number") {
        setAbstractsCount(stageData.abstracts_count);
      }

      setAbstractMigration(stageData?.abstract_migration_status ?? null);
      if (Array.isArray(stageData?.discovery_imports)) {
        setDiscoveryImports(stageData.discovery_imports);
      }

      if (typeof stageData?.full_text_docs_count === "number") {
        setDocsTotal(stageData.full_text_docs_count);
      }

      if (Array.isArray(stageData?.uploaded_files)) {
        const mappedDocs = stageData.uploaded_files.map((f) => ({
          id: f.id,
          name: f.name,
          url: f.url || null,
          size: f.size ?? null,
          uploaded_at: f.uploaded_at ?? null,
          linked_study_id: f.linked_study_id ?? null,
          has_fulltext_decisions: Boolean(f.has_fulltext_decisions),
        }));

        setDocuments(mappedDocs);
        if (typeof stageData?.full_text_docs_count !== "number") {
          setDocsTotal(mappedDocs.length);
        }
      }

      setMappingChecked(true);
      setMappingValid(true);
    }, [stageData]);

    // Fetch discovery searches the user has access to (for "Import from Discovery")
    // discoveryRequestIdRef guards against out-of-order responses: if the user
    // types fast, an older (slower) request could resolve after a newer one and
    // overwrite the list with stale results. 
    const discoveryRequestIdRef = useRef(0);
    const DISCOVERY_PAGE_SIZE = 25;
    const [discoveriesHasMore, setDiscoveriesHasMore] = useState(false);
    // Once a search is selected — whether it was already linked from the
    // backend, or the user just picked one right now — the list collapses
    // to just that one row instead of dumping the whole list on the user.
    // Clicking "View other searches" flips showAllDiscoveries to reveal
    // everything, and "View less" flips it back.
    const [showAllDiscoveries, setShowAllDiscoveries] = useState(false);

    // `page`/`append` power the "View More" button: page 1 (the default)
    // replaces the list — used for the initial load and for every new search
    // term. Passing append=true (View More) fetches the next page and adds
    // it on to what's already loaded instead of replacing it.
    const fetchDiscoveries = async (searchTerm = "", page = 1, append = false) => {
      const requestId = ++discoveryRequestIdRef.current;
      if (append) {
        setDiscoveriesLoadingMore(true);
      } else {
        setDiscoveriesLoading(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Loading discovery searches…");
      }
      try {
        const res = await axios.post(
          "/pceapi/slr/discovery/list",
          { page, size: DISCOVERY_PAGE_SIZE, search: searchTerm || null, source_type: "PUBMED" },
          { headers },
        );
        if (requestId !== discoveryRequestIdRef.current) return; // a newer request has since started
        if (res.data?.status === "success") {
          const rows = res.data.data || [];
          const total = res.data.total ?? res.data.total_count ?? res.data.count;

          setDiscoveries((prev) => (append ? [...prev, ...rows] : rows));
          setDiscoveriesPage(page);

          if (typeof total === "number") {
            setDiscoveriesTotal(total);
            setDiscoveriesHasMore(page * DISCOVERY_PAGE_SIZE < total);
          } else {
            // Backend didn't send a total — fall back to inferring from page fullness
            setDiscoveriesHasMore(rows.length === DISCOVERY_PAGE_SIZE);
          }
        }
      } catch (err) {
        if (requestId !== discoveryRequestIdRef.current) return;
        // Non-blocking — discovery import is optional, so just leave the list empty
        console.error("Failed to load literature searches", err);
      } finally {
        if (requestId === discoveryRequestIdRef.current) {
          setDiscoveriesLoading(false);
          setDiscoveriesLoadingMore(false);
          setIsProcessingPopupVisible(false);
        }
      }
    };

    // Fetch on mount (no debounce) and on search-as-you-type (350ms debounce).
    const isDiscoveryFirstRender = useRef(true);
    useEffect(() => {
      if (dataSourceMode !== "discovery") return;
      const delay = isDiscoveryFirstRender.current ? 0 : 350;
      isDiscoveryFirstRender.current = false;
      const t = setTimeout(() => fetchDiscoveries(discoverySearchQuery), delay);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discoverySearchQuery, dataSourceMode]);

    // If editing a project that already has a linked search, reflect that as pre-selected
    useEffect(() => {
      const existingLink =
        editingProject?.linked_discovery_id ||
        stageData?.linked_discovery_id ||
        null;
      if (existingLink) {
        setDataSourceMode("discovery");
        setSelectedSearchId(existingLink);
      }
    }, [editingProject, stageData]);

    useEffect(() => {
      if (selectedSearchId) return; // don't override an explicit choice
      if (!discoveries.length) return;

      const currentProjectId = editingProject?.id;
      const currentProjectName = (
        editingProject?.project_name || editingProject?.name || ""
      ).trim();

      const match = discoveries.find((d) =>
        (d.linked_projects || []).some((lp) => {
          if (currentProjectId != null && (lp.id === currentProjectId || lp.fabric_id === currentProjectId)) {
            return true;
          }
          if (currentProjectName && lp.name === currentProjectName) {
            return true;
          }
          return false;
        })
      );

      if (match) {
        setDataSourceMode("discovery");
        setSelectedSearchId(match.id);
      }
    }, [discoveries, editingProject]);

    // Keep the selected-discovery display label in sync with whatever the
    // (possibly search-filtered) discoveries list currently holds. If the
    // selected discovery isn't in the currently loaded page/filter, the last
    // known label is kept rather than clearing it.
    useEffect(() => {
      if (!selectedSearchId) {
        setSelectedDiscoveryLabel(null);
        return;
      }
      const match = discoveries.find((d) => d.id === selectedSearchId);
      if (match) {
        setSelectedDiscoveryLabel(match.name || match.search_query);
      }
    }, [discoveries, selectedSearchId]);

    // Selected discovery always floats to the top of the list.
    const [orderedDiscoveries, setOrderedDiscoveries] = useState([]);
    useEffect(() => {
      const idx = discoveries.findIndex((d) => d.id === selectedSearchId);
      if (idx <= 0) {
        setOrderedDiscoveries(discoveries);
        return;
      }
      const reordered = discoveries.slice();
      const [pinned] = reordered.splice(idx, 1);
      reordered.unshift(pinned);
      setOrderedDiscoveries(reordered);
    }, [discoveries, selectedSearchId]);

    // Collapse the list down to just the already-linked search until the
    // user explicitly asks to see the rest — but not while they're actively
    // searching, since a search implies they're looking for something else.
    const isCollapsedToLinked =
      !!selectedSearchId && !showAllDiscoveries && !discoverySearchQuery.trim();
    const visibleDiscoveries = isCollapsedToLinked
      ? orderedDiscoveries.filter((d) => d.id === selectedSearchId)
      : orderedDiscoveries;

    const fabricId =
      editingProject?.id ||
      createdProjectMeta?.project_id ||
      createdProjectMeta?.fabricId ||
      null;

    const s3Path =
      createdProjectMeta?.s3_path || createdProjectMeta?.s3Path || null;

    // Step 1 (General Settings) - Unsaved changes: emit normalized payload to parent
    const normalizeGeneralForUnsaved = () => {
      const sortedFullText = (
        Array.isArray(fullTextFiles) ? [...fullTextFiles] : []
      )
        .map((f) => ({
          name: f?.name,
          size: f?.size,
          lastModified: f?.lastModified,
        }))
        .sort(
          (a, b) =>
            (a.name || "").localeCompare(b.name || "") ||
            (a.size ?? 0) - (b.size ?? 0),
        );

      return {
        project_name: (formData.project_name || "").trim(),
        project_description: (formData.project_description || "").trim(),
        access_level: formData.access_level || "PRIVATE",
        team_id: formData.team_id ?? null,
        abstract_csv: abstractCsvFile
          ? {
            name: abstractCsvFile.name,
            size: abstractCsvFile.size,
            lastModified: abstractCsvFile.lastModified,
          }
          : null,
        full_text_files: sortedFullText,
      };
    };

    useEffect(() => {
      if (typeof onDataChange !== "function") return;
      onDataChange(normalizeGeneralForUnsaved());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, abstractCsvFile, fullTextFiles]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((p) => ({ ...p, [name]: value }));
    };

    const applyTeamSelection = (teamId, teamName) => {
      setFormData((p) => ({ ...p, team_id: teamId }));
      setSelectedTeamName(teamId ? (teamName ?? null) : null);
    };

    const handleTeamSelection = (teamId, teamName) => {
      // Selecting a team only updates the form. If the change would add
      // reviewers to the new team, the user is asked on Save (see handleSubmit).
      if (teamId && teamId === formData.team_id) return;
      applyTeamSelection(teamId || null, teamId ? teamName : null);
    };

    /**
     * On Save of an existing project whose team is being changed: find reviewers
     * who have papers assigned on this project but are not in the new team.
     * The backend adds them to the new team on save, so the user confirms first.
     * Reads the saved state fresh from the server, so it is never stale.
     * Returns [{ user_id, email }] — empty when there is nothing to confirm.
     */
    const findReviewersToAdd = async () => {
      const newTeamId = formData.team_id;
      if (!fabricId || !newTeamId) return [];

      const detailsRes = await axios.post(
        "/pceapi/slr/form/get-project-details",
        { slr_fabric_id: fabricId },
        { headers },
      );
      const stages = detailsRes.data?.data?.stages || {};
      const savedTeamId = stages.general_settings?.data?.team_id ?? null;
      if (String(savedTeamId) === String(newTeamId)) return [];

      const fw = stages.framework_selection?.data || {};
      const byId = new Map();
      [...(fw.phase1_human_allocations || []), ...(fw.phase2_human_allocations || [])]
        .forEach((a) => {
          if (a?.reviewer_id && !byId.has(a.reviewer_id)) {
            byId.set(a.reviewer_id, { user_id: a.reviewer_id, email: a.email || a.reviewer_id });
          }
        });
      if (!byId.size) return [];

      // Same endpoint the reviewer picker uses (/teams/details/{id} is blocked by
      // IAM because the team id is part of its path).
      const teamRes = await axios.post(
        "/pceapi/slr/form/team-members",
        { team_id: Number(newTeamId), page: 1, size: 1000 },
        { headers },
      );
      if (teamRes.data?.status !== "success") {
        throw new Error(teamRes.data?.message || "Could not load team members");
      }
      const memberIds = new Set(
        (teamRes.data?.data?.team_members || []).map((m) => String(m.user_id ?? m.id)),
      );
      return [...byId.values()].filter((r) => !memberIds.has(String(r.user_id)));
    };

    // Resolves the promise handleSubmit is waiting on: true = save, false = cancel.
    const teamConfirmResolverRef = useRef(null);

    const confirmTeamChange = () => {
      setPendingTeamChange(null);
      teamConfirmResolverRef.current?.(true);
      teamConfirmResolverRef.current = null;
    };

    const cancelTeamChange = () => {
      setPendingTeamChange(null);
      teamConfirmResolverRef.current?.(false);
      teamConfirmResolverRef.current = null;
    };

    const clearAbstractFile = () => {
      setAbstractCsvFile(null);
      setMappingChecked(false);
      setMappingValid(false);
      setMappingMessage("");
      setMissingTitleMessage("");
      if (abstractFileRef.current) {
        abstractFileRef.current.value = "";
        abstractFileRef.current.type = "";
        abstractFileRef.current.type = "file";
      }
    };

    const changeCsvSourceType = (next) => {
      setCsvSourceType(next);
      const name = (abstractCsvFile?.name || "").toLowerCase();
      if (name && !SOURCE_EXTENSIONS[next].some((ext) => name.endsWith(ext))) {
        clearAbstractFile();
      }
    };

    const onAbstractFileChange = (e) => {
      if (!e.target.files || e.target.files.length === 0) {
        clearAbstractFile();
        return;
      }
      const file = e.target.files[0];
      const name = file.name.toLowerCase();
      const allowed = SOURCE_EXTENSIONS[csvSourceType];
      if (!allowed.some((ext) => name.endsWith(ext))) {
        setMappingChecked(true);
        setMappingValid(false);
        setMappingMessage(
          `${SOURCE_GUIDANCE[csvSourceType].label} accepts ${allowed.join(", ")}. ` +
          `Switch the source database above if this file came from another database.`
        );
        clearAbstractFile();
        return;
      }

      setAbstractCsvFile(file);
      setMappingChecked(false);
      setMappingValid(false);
      setMappingMessage("");
      setMappingFormat("");
      setMappingWarnings([]);
      setMappingSourceMismatch(false);
      setMissingTitleMessage("");
    };

    const checkAbstractCsvMapping = async () => {
      if (!abstractCsvFile) {
        setMappingChecked(true);
        setMappingValid(false);
        setMappingMessage("Please choose a file first.");
        return;
      }

      setIsProcessingPopupVisible(true);
      setProcessingMessage("Checking file");

      try {
        const fd = new FormData();
        fd.append("csv_file", abstractCsvFile, abstractCsvFile.name);

        const res = await axios.post(`${API_BASE_URL}/form/check-csv-columns`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });

        const data = res.data;

        if (data.status === "NotAuthorized") {
          setIsProcessingPopupVisible(false);
          authNavigate("/login", "iam", navigate);
          return;
        }

        setMappingChecked(true);

        if (data.status === "failed") {
          setMappingValid(false);
          setMappingFormat("");
          setMappingWarnings([]);
          setMappingSourceMismatch(false);
          setMissingTitleMessage("");
          // The backend writes these for the person who has to fix the file --
          // which columns are missing, which line is malformed, which export to
          // download instead -- so show it verbatim.
          setMappingMessage(data.message || "This file could not be read.");
          setIsProcessingPopupVisible(false);
          return;
        }

        setMappingFormat(data.detected_format || "");
        setMappingWarnings(data.warnings || []);
        setMissingTitleMessage(data.missing_title_message || "");

        const hasMismatch =
          Boolean(data.source_database) && data.source_database !== csvSourceType;
        setMappingSourceMismatch(hasMismatch);

        if (hasMismatch) {
          setMappingValid(false);
          const detectedLabel =
            SOURCE_GUIDANCE[data.source_database]?.label || data.source_database;
          const selectedLabel =
            SOURCE_GUIDANCE[csvSourceType]?.label || csvSourceType;
          setMappingMessage(
            `This file looks like a ${detectedLabel} export, but you selected ${selectedLabel}. ` +
            `Switch the source database above or upload the correct file.`
          );
        } else {
          setMappingValid(true);
          setMappingMessage(
            `${data.records_parsed} record${data.records_parsed === 1 ? "" : "s"} read from ` +
              `${FORMAT_LABELS[data.detected_format] || "this file"}.`
          );
        }
      } catch (err) {
        setMappingChecked(true);
        setMappingValid(false);
        setMappingMessage(
          err?.response?.data?.message ||
          err.message ||
          "Failed to check CSV mapping",
        );
        clearAbstractFile();
      } finally {
        setIsProcessingPopupVisible(false);
      }
    };

    const uploadAbstractCsv = async () => {
      if (!abstractCsvFile) {
        setPopupErrorMessage("Please choose a file first.");
        setIsErrorPopupVisible(true);
        return;
      }
      if (!mappingChecked || !mappingValid) {
        setPopupErrorMessage("Please check the file before uploading.");
        setIsErrorPopupVisible(true);
        return;
      }

      const projectName = (formData.project_name || "").trim();
      let targetFabricId = fabricId;

      setIsProcessingPopupVisible(true);

      // ── Step 1: no project yet → create one first (metadata only, no CSV) ──
      if (!targetFabricId) {
        if (!projectName) {
          setIsProcessingPopupVisible(false);
          setPopupErrorMessage("Project name is required to upload.");
          setIsErrorPopupVisible(true);
          return;
        }

        setProcessingMessage("Creating project");

        try {
          const fdCreate = new FormData();
          fdCreate.append("name", projectName);
          fdCreate.append("fabric_access_level", formData.access_level);
          const description = (formData.project_description || "").trim();
          if (description) fdCreate.append("description", description);
          if (formData.team_id) fdCreate.append("team_id", String(formData.team_id));

          const createRes = await axios.post(`${API_BASE_URL}/form/create`, fdCreate, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
          });

          const createData = createRes.data;

          if (createData.status === "NotAuthorized") {
            setIsProcessingPopupVisible(false);
            authNavigate("/login", "iam", navigate);
            return;
          }
          if (createData.status === "failed") {
            setIsProcessingPopupVisible(false);
            setPopupErrorMessage(createData.message || "Failed to create project");
            setIsErrorPopupVisible(true);
            return;
          }

          targetFabricId = pickFabricId(createData);
          if (!targetFabricId) {
            setIsProcessingPopupVisible(false);
            setPopupErrorMessage("Failed to get project ID after creation.");
            setIsErrorPopupVisible(true);
            return;
          }

          setCreatedProjectMeta({
            project_id: targetFabricId,
            fabricId: targetFabricId,
            s3_path: createData?.payload?.s3_path || null,
          });

          // Notify parent so the LandingPage gets the new fabricId
          const normalizedCreate = {
            ...createData,
            fabric_id: targetFabricId,
            project_id: targetFabricId,
            team_id: formData.team_id ?? null,
            payload: createData?.payload || null,
          };
          onSuccess?.(createData.message || "Project created", normalizedCreate);

        } catch (err) {
          setIsProcessingPopupVisible(false);
          setPopupErrorMessage(
            err?.response?.data?.message || err.message || "Failed to create project"
          );
          setIsErrorPopupVisible(true);
          return;
        }
      }

      // ── Step 2: upload CSV via update-project ──
      setProcessingMessage("Uploading file");

      try {
        const fdUpdate = new FormData();
        fdUpdate.append("slr_fabric_id", String(targetFabricId));
        fdUpdate.append("stage", "general_settings");
        fdUpdate.append("csv_file", abstractCsvFile);
        fdUpdate.append(
          "data",
          JSON.stringify({
            name: projectName,
            description: (formData.project_description || "").trim() || null,
            fabric_access_level: formData.access_level,
            team_id: formData.team_id ?? null,
          }),
        );

        const res = await axios.post(`${API_BASE_URL}/form/update-project`, fdUpdate, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });

        const data = res.data;

        if (data.status === "NotAuthorized") {
          setIsProcessingPopupVisible(false);
          authNavigate("/login", "iam", navigate);
          return;
        }
        if (data.status === "failed") {
          setIsProcessingPopupVisible(false);
          setPopupErrorMessage(data.message || "Failed to upload file");
          setIsErrorPopupVisible(true);
          return;
        }

        // Clear abstract file state
        setAbstractCsvFile(null);
        setMappingChecked(false);
        setMappingValid(false);
        setMappingMessage("");
        setMappingFormat("");
        setMappingWarnings([]);
        setMappingSourceMismatch(false);
        setMissingTitleMessage("");
        if (abstractFileRef.current) {
          abstractFileRef.current.value = "";
          abstractFileRef.current.type = "";
          abstractFileRef.current.type = "file";
        }

        if (typeof data?.payload?.abstracts_count === "number") {
          setAbstractsCount(data.payload.abstracts_count);
        }

        setIsProcessingPopupVisible(false);
        setPopupSuccessMessage(data.message || "File uploaded successfully");
        setIsSuccessPopupVisible(true);

      } catch (err) {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(
          err?.response?.data?.message || err.message || "Failed to upload file"
        );
        setIsErrorPopupVisible(true);
      }
    };

    const onFullTextFilesChange = (e) => {
      const files = Array.from(e.target.files || []);
      const pdfs = files.filter((f) => f.name.toLowerCase().endsWith(".pdf"));

      const existingNames = new Set(fullTextFiles.map((f) => f.name));
      const newPdfs = pdfs.filter((f) => !existingNames.has(f.name));

      setFullTextFiles((prev) => [...prev, ...newPdfs]);

      const pendingDocs = newPdfs.map((f) => ({
        id: `local-${f.name}-${f.size}-${f.lastModified}`,
        name: f.name,
        url: URL.createObjectURL(f),
        _local: true,
      }));

      setDocuments((prev) => {
        const merged = [...prev, ...pendingDocs];
        setDocsTotal(merged.length);
        return merged;
      });
    };

    // -------- Documents list UI
    const toggleDocSelected = (docId) => {
      setSelectedDocIds((prev) => {
        const next = new Set(prev);
        if (next.has(docId)) next.delete(docId);
        else next.add(docId);
        return next;
      });
    };

    const toggleSelectAllVisible = (checked, visibleDocs) => {
      setSelectedDocIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          visibleDocs.forEach((d) => next.add(d.id));
        } else {
          visibleDocs.forEach((d) => next.delete(d.id));
        }
        return next;
      });
    };

    const filteredDocs = useMemo(() => {
      const q = docSearch.trim().toLowerCase();
      if (!q) return documents;
      return documents.filter((d) => (d.name || "").toLowerCase().includes(q));
    }, [documents, docSearch]);

    const visibleDocsAllSelected =
      filteredDocs.length > 0 &&
      filteredDocs.every((d) => selectedDocIds.has(d.id));

    const deleteSelectedDocuments = async (docIdsOverride) => {
      let idsToDelete = docIdsOverride?.length
        ? docIdsOverride
        : Array.from(selectedDocIds);

      if (idsToDelete.length === 0) return;

      const makeLocalId = (f) => `local-${f.name}-${f.size}-${f.lastModified}`;
      const isLocalId = (id) => String(id || "").startsWith("local-");

      const localIds = idsToDelete.filter(isLocalId);
      const remoteIds = idsToDelete.filter((id) => !isLocalId(id));

      const removeLocalDocs = (localIdsToRemove) => {
        if (!localIdsToRemove?.length) return;

        const localSet = new Set(localIdsToRemove);

        setDocuments((prev) => prev.filter((d) => !localSet.has(d.id)));

        setSelectedDocIds((prev) => {
          const next = new Set(prev);
          localIdsToRemove.forEach((id) => next.delete(id));
          return next;
        });

        setFullTextFiles((prev) =>
          prev.filter((f) => !localSet.has(makeLocalId(f))),
        );
      };

      if (!fabricId) {
        if (remoteIds.length > 0) {
          setPopupErrorMessage(
            "Please save the project first before deleting uploaded documents.",
          );
          setIsErrorPopupVisible(true);
          return;
        }
        removeLocalDocs(localIds);
        setDocsTotal((t) => Math.max(0, t - localIds.length));
        return;
      }

      // Remote deletions (and local cleanup if any)
      setIsProcessingPopupVisible(true);
      setProcessingMessage("Deleting selected documents");

      try {
        let refused = [];
        if (remoteIds.length > 0) {
          const results = await Promise.all(
            remoteIds.map((docId) =>
              axios.delete(`${API_BASE_URL}/full-text-screening/delete`, {
                headers: { ...headers },
                data: { fabric_id: fabricId, doc_id: docId },
              }),
            ),
          );
          // The API answers 200 with status "failed" when it refuses (e.g. the
          // full-text QC lock) — keep those documents in the list.
          refused = results
            .map((res, i) => ({ id: remoteIds[i], data: res?.data }))
            .filter((r) => r.data?.status === "failed");
        }

        if (refused.length > 0) {
          const refusedSet = new Set(refused.map((r) => r.id));
          idsToDelete = idsToDelete.filter((id) => !refusedSet.has(id));
          setPopupErrorMessage(
            refused[0].data?.message ||
              `${refused.length} document(s) could not be deleted.`,
          );
          setIsErrorPopupVisible(true);
        }

        const idsSet = new Set(idsToDelete);
        setDocuments((prev) => prev.filter((d) => !idsSet.has(d.id)));

        setSelectedDocIds((prev) => {
          const next = new Set(prev);
          idsToDelete.forEach((id) => next.delete(id));
          return next;
        });

        setDocsTotal((t) => Math.max(0, t - idsToDelete.length));

        removeLocalDocs(localIds);
      } catch (err) {
        const data = err?.response?.data;

        if (data?.status === "NotAuthorized") {
          setIsProcessingPopupVisible(false);
          authNavigate("/login", "iam", navigate);
          return;
        }

        setMappingMessage(
          data?.message || err.message || "Failed to delete selected documents",
        );
      } finally {
        setIsProcessingPopupVisible(false);
      }
    };

    const downloadSampleCsv = async () => {
      try {
        // One template serves every source; /form/sample-csv/download takes no
        // parameters.
        const res = await axios.get(`${API_BASE_URL}/form/sample-csv/download`, {
          headers,
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sample_csv.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const data = err?.response?.data;

        if (data?.status === "NotAuthorized") {
          authNavigate("/login", "iam", navigate);
          return;
        }

        setPopupErrorMessage(
          data?.message || err.message || "Failed to download sample CSV",
        );
        setIsErrorPopupVisible(true);
      }
    };

    // -------- Delete Abstract Data
    const closeDeleteAbstractsModal = () => {
      setShowDeleteAbstractsModal(false);
      setDeleteAbstractsConfirmText("");
      setAbstractsPreviewOpen(false);
      setAbstractsPreviewList([]);
      setAbstractsPreviewSearch("");
      setAbstractsPreviewPage(1);
      setAbstractsPreviewTotal(0);
    };

    // `page`/`append` follow the same pattern as fetchDiscoveries above:
    // page 1 (default) replaces the list — used for the initial open and
    // for every new search term. append=true (View More) fetches the next
    // page and adds it on, so the person can reach every abstract, not just
    // the first page.
    const fetchAbstractsPreview = async (searchTerm = "", page = 1, append = false) => {
      if (!fabricId) return;
      if (append) {
        setAbstractsPreviewLoadingMore(true);
      } else {
        setAbstractsPreviewLoading(true);
      }
      try {
        const res = await axios.post(
          `${API_BASE_URL}/form/list-uploaded-abstracts`,
          {
            slr_fabric_id: fabricId,
            page,
            size: ABSTRACTS_PREVIEW_PAGE_SIZE,
            search: searchTerm || null,
          },
          { headers },
        );
        if (res.data?.status === "success") {
          const rows = res.data.data?.abstracts || [];
          setAbstractsPreviewList((prev) => (append ? [...prev, ...rows] : rows));
          setAbstractsPreviewTotal(res.data.data?.total || 0);
          setAbstractsPreviewPage(page);
        } else {
          setPopupErrorMessage(res.data?.message || "Failed to load abstracts");
          setIsErrorPopupVisible(true);
        }
      } catch (err) {
        setPopupErrorMessage(
          err?.response?.data?.message || err.message || "Failed to load abstracts",
        );
        setIsErrorPopupVisible(true);
      } finally {
        setAbstractsPreviewLoading(false);
        setAbstractsPreviewLoadingMore(false);
      }
    };

    // Opens the standalone, wider "Uploaded Abstracts" table modal (separate
    // from the DeleteConfirmationModal) so the grid gets real room instead of
    // being squeezed inside the confirm dialog.
    const openAbstractsPreview = () => {
      setShowDeleteAbstractsModal(false); // close the confirm dialog underneath
      setAbstractsPreviewOpen(true);
      if (abstractsPreviewList.length === 0) fetchAbstractsPreview("", 1, false);
    };

    const closeAbstractsPreview = () => {
      setAbstractsPreviewOpen(false);
      setShowDeleteAbstractsModal(true); // bring the confirm dialog back
    };

    const confirmDeleteAbstractData = async () => {
      if (!fabricId) return;
      if (deleteAbstractsConfirmText.trim() !== (formData.project_name || "").trim()) {
        setPopupErrorMessage("The project name you typed doesn't match.");
        setIsErrorPopupVisible(true);
        return;
      }

      // Same pattern as ThreadList.jsx's handleDelete: close the confirm modal
      // first, then run the request behind ProcessingPopup.
      setShowDeleteAbstractsModal(false);
      setIsProcessingPopupVisible(true);
      setProcessingMessage("Deleting abstract data");

      try {
        const res = await axios.post(
          `${API_BASE_URL}/form/delete-abstract-data`,
          { slr_fabric_id: fabricId, confirm_name: deleteAbstractsConfirmText.trim() },
          { headers },
        );
        if (res.data?.status === "NotAuthorized") {
          setIsProcessingPopupVisible(false);
          authNavigate("/login", "iam", navigate);
          return;
        }
        if (res.data?.status !== "success") {
          setIsProcessingPopupVisible(false);
          setPopupErrorMessage(res.data?.message || "Failed to delete abstract data");
          setIsErrorPopupVisible(true);
          return;
        }

        setAbstractsCount(0);
        setMigrationWarning("");
        setDeleteAbstractsConfirmText("");
        setAbstractsPreviewOpen(false);
        setAbstractsPreviewList([]);
        setAbstractsPreviewSearch("");
        setAbstractsPreviewPage(1);
        setAbstractsPreviewTotal(0);

        const deletedCount = res.data.deleted_count ?? 0;
        const cascadedCount = res.data.cascaded_fulltext_count ?? 0;
        // The user was told in the confirm dialog that all screening data goes
        // with the abstracts, so this is a normal success, not a warning.
        let message =
          `Deleted ${deletedCount} abstract${deletedCount === 1 ? "" : "s"} and all screening data.`;
        if (cascadedCount > 0) {
          message +=
            ` This included ${cascadedCount} full-text screening ` +
            `record${cascadedCount === 1 ? "" : "s"}. Uploaded PDF files were not deleted.`;
        }
        setPopupSuccessMessage(message);
        setIsSuccessPopupVisible(true);
      } catch (err) {
        setPopupErrorMessage(
          err?.response?.data?.message || err.message || "Failed to delete abstract data",
        );
        setIsErrorPopupVisible(true);
      } finally {
        setIsProcessingPopupVisible(false);
      }
    };

    const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

    // Documents in the pending delete that a paper is linked to. Deleting
    // them unlinks the paper and wipes its full-text screening decisions.
    const pendingDeleteImpact = useMemo(() => {
      const ids = new Set(pendingDeleteIds);
      const docs = documents.filter((d) => ids.has(d.id));
      return {
        linked: docs.filter((d) => d.linked_study_id != null),
        withDecisions: docs.filter((d) => d.has_fulltext_decisions),
      };
    }, [pendingDeleteIds, documents]);

    const openDeleteModal = (ids, name = "") => {
      setPendingDeleteIds(ids);
      setPendingDeleteName(name);
      setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
      setShowDeleteModal(false);
      setPendingDeleteIds([]);
      setPendingDeleteName("");
    };

    const confirmDelete = async () => {
      const ids = pendingDeleteIds;
      closeDeleteModal();
      await deleteSelectedDocuments(ids);
    };

    const openPreview = (doc) => {
      const idStr = String(doc?.id ?? "");
      const isLocal = doc?._local === true || idStr.startsWith("local-");

      if (isLocal) {
        setLocalPreviewLoading(true);
        setLocalPreviewUrl(doc.url || null);
        setLocalPreviewName(doc.name || "Document");
        setPreviewOpen(true);
        return;
      }

      setPreviewDocId(Number(doc.id));
      setPreviewDocName(doc.name);
      setLocalPreviewUrl(null);
      setLocalPreviewName(null);
      setLocalPreviewLoading(false);
      setPreviewOpen(true);
    };

    // 3) Update closePreview (loading reset)
    const closePreview = () => {
      setPreviewOpen(false);
      setPreviewDocId(null);
      setPreviewDocName("");
      setLocalPreviewUrl(null);
      setLocalPreviewName(null);
      setLocalPreviewLoading(false);
    };

    const handleSubmit = async (e) => {
      e?.preventDefault?.();

      const projectName = (formData.project_name || "").trim();
      if (!projectName) {
        setPopupErrorMessage("Project name is required");
        setIsErrorPopupVisible(true);
        return;
      }

      // Team changed and some assigned reviewers are not in the new team →
      // they will be added to it on save, so confirm with the user first.
      let reviewersToAdd = [];
      try {
        reviewersToAdd = await findReviewersToAdd();
      } catch (err) {
        console.error("Reviewer/team check failed:", err);
      }
      if (reviewersToAdd.length > 0) {
        // A previous Save still waiting on this popup is treated as cancelled.
        teamConfirmResolverRef.current?.(false);
        const confirmed = await new Promise((resolve) => {
          teamConfirmResolverRef.current = resolve;
          setPendingTeamChange({
            teamId: formData.team_id,
            teamName: selectedTeamName,
            nonMembers: reviewersToAdd,
          });
        });
        if (!confirmed) return false;
      }

      setIsProcessingPopupVisible(true);
      setProcessingMessage(
        editingProject
          ? "Updating SLR project"
          : fabricId
            ? "Saving changes"
            : "Creating SLR project",
      );

      try {
        let res;

        if (fabricId) {
          // UPDATE mode
          const fdUpdate = new FormData();
          fdUpdate.append("slr_fabric_id", String(fabricId));
          fdUpdate.append("stage", "general_settings");

          fdUpdate.append(
            "data",
            JSON.stringify({
              name: projectName,
              description: (formData.project_description || "").trim() || null,
              fabric_access_level: formData.access_level,
              team_id: formData.team_id ?? null,
            }),
          );

          fullTextFiles.forEach((f) => fdUpdate.append("files", f));

          res = await axios.post(`${API_BASE_URL}/form/update-project`, fdUpdate, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
          });
        } else {
          // CREATE mode
          const fdCreate = new FormData();
          fdCreate.append("name", projectName);
          fdCreate.append("fabric_access_level", formData.access_level);

          const description = (formData.project_description || "").trim();
          if (description) fdCreate.append("description", description);

          if (formData.team_id)
            fdCreate.append("team_id", String(formData.team_id));

          fullTextFiles.forEach((f) => fdCreate.append("pdf_files", f));

          res = await axios.post(`${API_BASE_URL}/form/create`, fdCreate, {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
          });
        }

        const data = res.data;

        if (data.status === "NotAuthorized") {
          setIsProcessingPopupVisible(false);
          authNavigate("/login", "iam", navigate);
          return;
        }

        if (data.status === "failed") {
          setIsProcessingPopupVisible(false);
          setPopupErrorMessage(data.message || "Failed to save project");
          setIsErrorPopupVisible(true);
          return;
        }

        setFullTextFiles([]);

        if (fullTextRef.current) {
          fullTextRef.current.value = "";
        }

        const normalizedFabricId = pickFabricId(data) || null;

        const normalizedResponse = {
          ...data,
          fabric_id: normalizedFabricId,
          project_id: normalizedFabricId,
          team_id: formData.team_id ?? null,
          payload: data?.payload || null,
        };

        let refreshedStageData = null;
        const linkedFabricId = normalizedFabricId || fabricId;

        if (dataSourceMode === "discovery" && selectedSearchId && linkedFabricId
            && abstractMigration !== "COMPLETED" && !migrationInFlightRef.current) {
          migrationInFlightRef.current = true;
          setProcessingMessage("Importing discovery");

          try {
            const migRes = await axios.post(
              "/pceapi/slr/discovery/migrate-full-search",
              {
                fabric_id: linkedFabricId,
                search_id: selectedSearchId,
                migration_type: "abstract",
              },
              { headers },
            );
            if (migRes.data?.status !== "success") {
              setMigrationWarning(
                migRes.data?.message ||
                "Project was created, but importing the search failed. You can retry from the Discovery stage.",
              );
            } else {
              setMigrationWarning("");
              setAbstractMigration("COMPLETED");
            }
          } catch (migErr) {
            setMigrationWarning(
              migErr?.response?.data?.message ||
              "Project was created, but importing the search failed. You can retry from the Discovery stage.",
            );
          } finally {
            migrationInFlightRef.current = false;
          }

          // One refresh of the fabric's project details so a screening
          // protocol generated as a side effect of linking (see backend)
          // shows up immediately, instead of only appearing after some
          // unrelated navigation forces a refetch.
          try {
            const detailsRes = await axios.post(
              "/pceapi/slr/form/get-project-details",
              { slr_fabric_id: linkedFabricId },
              { headers },
            );
            if (detailsRes.data?.status === "success") {
              refreshedStageData = detailsRes.data.data;
            }
          } catch (detailsErr) {
            console.error("Failed to refresh project details after import", detailsErr);
          }
        }

        if (refreshedStageData) {
          normalizedResponse.refreshed_stage_data = refreshedStageData;
        }

        setIsProcessingPopupVisible(false);
        onSuccess?.(data.message || "Project saved", normalizedResponse);

        if (!fabricId) {
          setCreatedProjectMeta({
            project_id: normalizedFabricId,
            fabricId: normalizedFabricId,
            s3_path: data?.payload?.s3_path || null,
          });
        }

        if (typeof data?.payload?.abstracts_count === "number")
          setAbstractsCount(data.payload.abstracts_count);
        if (Array.isArray(data?.payload?.documents)) {
          setDocuments(data.payload.documents);
          setDocsTotal(data.payload.documents.length);
        }

        return true;
      } catch (err) {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(
          err?.response?.data?.message ||
          err.message ||
          "Failed to save project",
        );
        setIsErrorPopupVisible(true);
        return false;
      }
    };

    // ✅ FIX: LandingPage will call this to trigger Step-1 save/create
    useImperativeHandle(ref, () => ({
      submitGeneralSettings: () => handleSubmit(),
    }));

    const closeErrorPopup = () => setIsErrorPopupVisible(false);
    // -------------------------
    // UI
    // -------------------------
    return (
       <div className="w-100 slr-settings-root scrollable-element pe-1">
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* LEFT */}
            <div className="col-6 pe-4">
              <div className="mb-2">
                <label className="ev-reg-p-dark mb-1">
                  Project Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="formField6"
                  style={{ outline: "none" }}
                  name="project_name"
                  value={formData.project_name}
                  onChange={handleInputChange}
                  placeholder="Enter your project name here"
                />
              </div>

              <div className="mb-2">
                <label className="ev-reg-p-dark mb-1">
                  Project Description{" "}
                  <span className="ev-small-p-blue">
                    <i>(Optional)</i>
                  </span>
                </label>
                <textarea
                  className="formField6 vh-8"
                  style={{ outline: "none" }}
                  name="project_description"
                  value={formData.project_description}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder="Enter your project description here"
                />
              </div>

              <div className="margin-bottom-3 w-50">
                <label className="ev-reg-p-dark mb-1">
                  Access Level <span className="text-danger">*</span>
                </label>
                <div
                  className="btn-group custom-btn-group-outer w-100"
                  role="group"
                  aria-label="Access Level Selection"
                >
                  <button
                    type="button"
                    className={`btn custom-btn-group-inner ${formData.access_level === "PUBLIC"
                      ? "ev-btn-blue-fill-round"
                      : "ev-btn-lightblue-fill-round"
                      }`}
                    onClick={() =>
                      setFormData((p) => ({ ...p, access_level: "PUBLIC" }))
                    }
                  >
                    Public
                  </button>
                  <button
                    type="button"
                    className={`btn custom-btn-group-inner ${formData.access_level === "PRIVATE"
                      ? "ev-btn-blue-fill-round"
                      : "ev-btn-lightblue-fill-round"
                      }`}
                    onClick={() =>
                      setFormData((p) => ({ ...p, access_level: "PRIVATE" }))
                    }
                  >
                    Private
                  </button>
                </div>
              </div>

              <div className="col-12">
                {/* Controlled via `openLeftSection` (instead of `defaultOpen`) so
                    opening this section forces "Full-Text Screening Documents"
                    below it to close — see the shared state's declaration for why. */}
                <Section
                  title={<>Project Team <span className="ev-small-p-blue"><i>(Optional)</i></span>{formData.team_id && <span className="ms-2 ev-small-p-blue">· {selectedTeamName || "Team selected"}</span>}</>}
                  open={openLeftSection === "team"}
                  onToggle={() => setOpenLeftSection((v) => (v === "team" ? null : "team"))}
                >
                  <div className="ev-radius-small vh-20 p-0" style={{ border: '1px solid var(--shadow2)' }}>
                    <FormTeamsList
                      onSelectTeam={handleTeamSelection}
                      selectedTeamId={formData.team_id}
                      apiBaseUrl="/pceapi/fabrics"
                    />
                  </div>
                </Section>

                {/* Moved here from the right ("Data Source") column — now sits
                    directly under Project Team, and shares `openLeftSection`
                    with it above so only one of the two can be expanded at a time. */}
                <Section
                  title={<>Full-Text Screening Documents <span className="ev-small-p-blue"><i>(Optional)</i></span>{documents.length > 0 && <span className="ms-2               ev-small-p-blue">· {docsTotal || documents.length} uploaded</span>}</>}
                  open={openLeftSection === "fulltext"}
                  onToggle={() => setOpenLeftSection((v) => (v === "fulltext" ? null : "fulltext"))}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="ev-small-p-blue">Upload PDFs</span>
                    <label className="d-flex align-items-center gap-2 mb-0">
                      <input
                        type="file"
                        className="form-control form-control-sm choose-files"
                        accept=".pdf"
                        multiple
                        ref={fullTextRef}
                        onChange={onFullTextFilesChange}
                      />
                    </label>
                  </div>

                  {/* Uploaded documents — lightweight when empty, full manager once files exist */}
                  {documents.length === 0 && filteredDocs.length === 0 ? (
                    <div className="ev-small-p-dark text-muted d-flex align-items-center gap-2 px-1 py-2">
                      <i className="bi bi-file-earmark-pdf" />
                      No files uploaded yet — choose PDFs above to add them.
                    </div>
                  ) : (
                    <div
                      className="ev-card-4 ev-card-8 ev-radius-small py-2 pb-1 d-flex flex-column"
                      style={{ maxHeight: "220px" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-0 px-2">
                        <p className="ev-med-p-blue mb-1">Uploaded Documents</p>
                      </div>

                      <div className="d-flex align-items-center gap-2 flex-nowrap px-2">
                        <input
                          type="checkbox"
                          checked={visibleDocsAllSelected}
                          onChange={(e) =>
                            toggleSelectAllVisible(e.target.checked, filteredDocs)
                          }
                          title="Select all visible"
                        />
                        <span className="ev-reg-p-blue ev-bold-med">
                          Documents ({docsTotal || documents.length})
                        </span>
                        <div className="position-relative w-60">
                          <input
                            type="text"
                            className="form-control search-input ms-0"
                            placeholder="Search document name"
                            value={docSearch}
                            onChange={(e) => setDocSearch(e.target.value)}
                          />
                          <button
                            type="button"
                            className="ev-btn-lightblue-fill-round search-bar-button"
                          >
                            <i className="bi bi-search" />
                          </button>
                        </div>

                        <button
                          type="button"
                          className="ev-btn-red-fill-round ev-btn-reg ms-auto"
                          onClick={() => {
                            const ids = Array.from(selectedDocIds);
                            if (ids.length === 0) return;
                            openDeleteModal(ids, "");
                          }}
                          disabled={selectedDocIds.size === 0}
                        >
                          Delete selected ({selectedDocIds.size})
                        </button>
                      </div>

                      {/* Bounded, independently-scrollable list — never grows the page */}
                      <div
                        className="scrollable-element overflow-auto mt-2 px-2"
                        style={{ maxHeight: "120px" }}
                      >
                        {filteredDocs.length === 0 ? (
                          <div className="text-muted small py-3">
                            No documents found.
                          </div>
                        ) : (
                          filteredDocs.slice(0, visibleCount).map((doc) => (
                            <div
                              key={doc.id}
                              className="d-flex align-items-center category-badge category-badge-5 justify-content-between py-0 border-0 ev-radius-xsmall"
                            >
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedDocIds.has(doc.id)}
                                  onChange={() => toggleDocSelected(doc.id)}
                                />
                                <i className="bi bi-file-earmark-pdf-fill text-danger" />
                                <span
                                  className="ev-reg-p-dark mb-0 ps-0 doc-name-ellipsis avatar-tooltip"
                                  data-tooltip={doc?.name || ""}
                                >
                                  {(doc?.name?.length > 45 ? `${doc.name.slice(0, 45)}...` : doc?.name) || ""}
                                </span>
                              </div>

                              <div className="d-flex align-items-center pt-1 gap-3">
                                <button
                                  type="button"
                                  className="category-badge category-badge-5 ev-btn-small d-flex align-items-center pt-1"
                                  onClick={() => openPreview(doc)}
                                  disabled={
                                    !doc?.id || (doc?._local ? !doc.url : !fabricId)
                                  }
                                >
                                  Preview
                                </button>

                                <button
                                  type="button"
                                  className="ev-btn-red-fill-round ev-radius-xsmall ev-btn-small d-flex align-items-center"
                                  onClick={() => openDeleteModal([doc.id], doc.name)}
                                  disabled={!doc?.id}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash3" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Load More Button - Anchored to the bottom */}
                      {filteredDocs.length > visibleCount && (
                        <div className="text-end mt-auto pt-2 px-2">
                          <button
                            type="button"
                            className="ev-btn-blue-outline-round ev-btn-small"
                            /* Increment visibleCount by 15 on each click */
                            onClick={() => setVisibleCount((prev) => prev + 15)}
                          >
                            Load More
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </Section>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-6 pe-4">
              <p className="ev-reg-p-dark ev-bold-med mb-2">Data Source</p>

              {/* 3-way source selector */}
              <div className="row g-2 mb-2">
                <div className="col-4">
                  <div
                    className={`ev-card-2 h-100 cursor-pointer ${dataSourceMode === "discovery" ? "active" : ""}`}
                    onClick={() => setDataSourceMode("discovery")}
                  >
                    <span className="ev-badge-green-gradient mb-1 d-inline-block">Recommended</span>
                    <p className="ev-reg-p-dark ev-bold-med mb-1">
                      <i className="bi bi-stars me-1" />
                      Import from Discovery
                    </p>
                    <p className="ev-small-p-dark text-muted mb-0">
                      Use a search you already ran
                    </p>
                  </div>
                </div>
                <div className="col-4">
                  <div
                    className={`ev-card-2 h-100 cursor-pointer ${dataSourceMode === "csv" ? "active" : ""}`}
                    onClick={() => setDataSourceMode("csv")}
                  >
                    <p className="ev-reg-p-dark ev-bold-med mb-1">
                      <i className="bi bi-upload me-1" />
                      Upload File Mannualy
                    </p>
                    <p className="ev-small-p-dark text-muted mb-0">
                      Bring in an external file
                    </p>
                  </div>
                </div>
                <div className="col-4">
                  <div
                    className={`ev-card-2 h-100 cursor-pointer ${dataSourceMode === "later" ? "active" : ""}`}
                    onClick={() => setDataSourceMode("later")}
                  >
                    <p className="ev-reg-p-dark ev-bold-med mb-1">
                      <i className="bi bi-skip-forward me-1" />
                      Add later
                    </p>
                    <p className="ev-small-p-dark text-muted mb-0">
                      Skip for now, add in Discovery stage
                    </p>
                  </div>
                </div>
              </div>

              {/* ---- Mode: Import from Discovery ---- */}
              {dataSourceMode === "discovery" && (
                <>
                  <p className="ev-small-p-dark mb-2 d-flex align-items-center gap-2">
                    {abstractMigration && (
                      <span className={
                        abstractMigration === "COMPLETED" ? "discovery-badge-green"
                        : abstractMigration === "FAILED" ? "discovery-badge-orange"
                        : "discovery-badge-unknown"
                      }>
                        {abstractMigration === "COMPLETED" ? "Abstracts imported"
                          : abstractMigration === "FAILED" ? "Import failed"
                          : abstractMigration === "NOT_STARTED" ? "Import not started"
                          : "Importing…"}
                        {abstractsCount > 0 && (
                          <span className="ms-1">{abstractsCount}</span>
                        )}
                      </span>
                    )}
                  </p>

                  {/* Always open — no collapse/toggle. Styled with the same
                      `discovery-section`/`discovery-section-header` classes as
                      the Project Team / Full-Text Screening dropdowns (see
                      Slr.css) so it looks like the same kind of control, but
                      the header is a static label (not a button) since there's
                      nothing to expand or collapse. If a search is already
                      linked to this project, the effects above pre-select it
                      and `orderedDiscoveries` floats it to the top of the list
                      below, so it's shown without any extra UI. */}
                  <div className="discovery-section mb-2">
                    <div className="discovery-section-header">
                      <span className="ev-small-p-dark ev-bold-med text-truncate">
                        View literature discoveries
                      </span>
                    </div>
                    <div className="discovery-section-body">
                    <div className="ev-radius-small p-2" style={{ border: '1px solid var(--shadow2)' }}>
                    <div className="position-relative mb-2 w-100">
                      <input
                        type="text"
                        className="form-control search-input ms-0"
                        placeholder="Search Literature Discoveries"
                        value={discoverySearchQuery}
                        onChange={(e) => setDiscoverySearchQuery(e.target.value)}
                      />
                      <button
                        type="button"
                        className="ev-btn-lightblue-fill-round search-bar-button"
                        onClick={() => fetchDiscoveries(discoverySearchQuery)}
                      >
                        <i className="bi bi-search" />
                      </button>
                    </div>
                    <div
                      className={`scrollable-element ev-radius-small p-2 ${isCollapsedToLinked ? "vh-10" : "vh-25 overflow-auto"}`}
                      style={isCollapsedToLinked ? undefined : { paddingRight: "14px" }}
                    >
                      {discoveriesLoading ? (
                        <p className="ev-small-p-dark text-muted text-center py-4 mb-0">
                          Loading your searches…
                        </p>
                      ) : discoveries.length === 0 ? (
                        <p className="ev-small-p-dark text-muted text-center py-4 mb-0">
                          No literature searches found.
                        </p>
                      ) : (
                        visibleDiscoveries.map((d) => {
                          const isSelected = selectedSearchId === d.id;
                          const linked = d.linked_projects || [];
                          const inputId = `discovery-search-${d.id}`;
                          const displayLabel = d.name || d.search_query;
                          return (
                            <label
                              key={d.id}
                              htmlFor={inputId}
                              className="ev-radius-small mb-2 cursor-pointer d-block"
                              style={{
                                padding: "10px 12px",
                                background: isSelected ? "var(--color10)" : "var(--background2)",
                                border: isSelected
                                  ? "1.5px solid var(--color9)"
                                  : "1px solid var(--shadow2)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  id={inputId}
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => setSelectedSearchId(d.id)}
                                />
                                {linked.length > 0 && (
                                  <span className="ev-alert-card-warn flex-shrink-0">LINKED</span>
                                )}
                                <span
                                  className="ev-reg-p-dark ev-bold-med mb-0 text-truncate"
                                  style={{ maxWidth: "260px" }}
                                >
                                  {displayLabel}
                                </span>
                                <span className="ms-auto ev-small-p-blue ev-bold-med">
                                  {(d.number_of_citations ?? 0).toLocaleString()} records
                                </span>
                              </div>
                              {linked.length > 0 && (
                                <p className="ev-small-p-dark text-muted mb-0 mt-1">
                                  <i>
                                    Already used in "{linked[0].name}"
                                    {linked.length > 1 ? ` and ${linked.length - 1} more` : ""} — reusing is fine.
                                  </i>
                                </p>
                              )}
                            </label>
                          );
                        })
                      )}
                    </div>

                    {isCollapsedToLinked ? (
                      <div className="text-center mt-2">
                        <button
                          type="button"
                          className="ev-btn-blue-outline-round ev-btn-small"
                          onClick={() => setShowAllDiscoveries(true)}
                        >
                          View other searches
                        </button>
                      </div>
                    ) : (
                      (discoveriesHasMore || (selectedSearchId && !discoverySearchQuery.trim())) && (
                        <div className="text-center mt-2 d-flex justify-content-center gap-2">
                          {selectedSearchId && !discoverySearchQuery.trim() && (
                            <button
                              type="button"
                              className="ev-btn-blue-outline-round ev-btn-small"
                              onClick={() => setShowAllDiscoveries(false)}
                            >
                              View less
                            </button>
                          )}
                          {discoveriesHasMore && (
                            <button
                              type="button"
                              className="ev-btn-blue-outline-round ev-btn-small"
                              onClick={() => fetchDiscoveries(discoverySearchQuery, discoveriesPage + 1, true)}
                              disabled={discoveriesLoadingMore}
                            >
                              {discoveriesLoadingMore ? "Loading…" : "View More"}
                            </button>
                          )}
                        </div>
                      )
                    )}
                    </div>
                    </div>
                  </div>

                  {migrationWarning && (
                    <div className="alert alert-warning py-2 mt-2 mb-0">
                      <i className="bi bi-exclamation-triangle me-2" />
                      {migrationWarning}
                    </div>
                  )}

                  <p className="ev-small-p-dark text-muted text-center mt-2 mb-3">
                    Don't see your search?{" "}
                    <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToDiscovery?.(); }}>
                      <b>+ Create a new search in Discovery</b>
                    </a>
                  </p>
                </>
              )}

              {/* ---- Mode: Upload CSV ---- */}
              {dataSourceMode === "csv" && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="ev-reg-p-dark mb-0">Source database</label>
                    <div className="ev-view-toggle-shell">
                    <button
                      type="button"
                      className={`ev-view-toggle-btn ${
                        csvSourceType === "PUBMED" ? "active" : ""
                      }`}
                      onClick={() => changeCsvSourceType("PUBMED")}
                    >
                      PubMed
                    </button>

                    <button
                      type="button"
                      className={`ev-view-toggle-btn ${
                        csvSourceType === "EMBASE" ? "active" : ""
                      }`}
                      onClick={() => changeCsvSourceType("EMBASE")}
                    >
                      Embase
                    </button>

                    <button
                      type="button"
                      className={`ev-view-toggle-btn ${
                        csvSourceType === "COCHRANE" ? "active" : ""
                      }`}
                      onClick={() => changeCsvSourceType("COCHRANE")}
                    >
                      Cochrane
                    </button>
                  </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-between w-100 p-2 pb-0 mb-1">
                    <label className="ev-reg-p-dark mb-0">
                      Upload Data for Abstract Screening{" "}
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <span className="category-badge category-badge-1">
                        {abstractsCount} Abstracts Present
                      </span>
                      {fabricId && abstractsCount > 0 && (
                        <button
                          type="button"
                          className="ev-btn-red-outline-round ev-btn-small"
                          style={{ marginBottom: "var(--px-12)" }}
                          onClick={() => setShowDeleteAbstractsModal(true)}
                        >
                          <i className="bi bi-trash3 me-1" />
                          Delete Abstract Data
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="input-group mb-2">
                    <input
                      type="file"
                      className="form-control form-control-sm choose-files"
                      accept={SOURCE_EXTENSIONS[csvSourceType].join(",")}
                      ref={abstractFileRef}
                      onChange={onAbstractFileChange}
                    />
                    <button
                      type="button"
                      className="ev-btn-blue-fill-round ev-btn-small"
                      onClick={checkAbstractCsvMapping}
                      disabled={!abstractCsvFile}
                    >
                      Check File
                    </button>
                    <button
                      type="button"
                      className="ev-btn-lightblue-fill-round ev-btn-small ms-1"
                      onClick={uploadAbstractCsv}
                      disabled={!abstractCsvFile || !mappingChecked || !mappingValid}
                    >
                      <i className="bi bi-upload me-1" />
                      Upload
                    </button>
                  </div>

                  {abstractCsvFile && mappingChecked && (
                    <div
                      className={`alert ${mappingValid ? "alert-success" : "alert-danger"} py-2`}
                    >
                      <i
                        className={`bi ${mappingValid ? "bi-check-circle" : "bi-x-circle"} me-2`}
                      />
                      {mappingMessage}
                    </div>
                  )}

                  {abstractCsvFile && mappingChecked && mappingValid && missingTitleMessage && (
                    <div className="alert alert-warning py-2">
                      <i className="bi bi-exclamation-triangle me-2" />
                      {missingTitleMessage}
                    </div>
                  )}

                  {/* The source-database mismatch notice is intentionally not
                      shown for now. mappingSourceMismatch and mappingFormat are
                      still computed, so restoring this is a matter of putting
                      the block back. */}

                  {/* The per-record parse warnings are intentionally not shown
                      for now. The backend still returns them and
                      mappingWarnings still holds them, so restoring this is a
                      matter of putting the block back. */}

                  {abstractCsvFile && !mappingChecked && (
                    <div className="alert alert-warning py-2">
                      <i className="bi bi-exclamation-triangle me-2" />
                      Please check the file before uploading.
                    </div>
                  )}

                  <div className="alert alert-primary py-2 mb-3 d-flex justify-content-between align-items-center px-2 pe-1">
                    <div>
                      <i className="bi bi-info-circle me-1" />
                      <b>{SOURCE_GUIDANCE[csvSourceType].label}:</b>{" "}
                      {SOURCE_GUIDANCE[csvSourceType].steps}
                    </div>
                    {csvSourceType === "PUBMED" && (
                      <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-btn-small flex-shrink-0 ms-2"
                        onClick={() => downloadSampleCsv()}
                      >
                        <i className="bi bi-download me-1" />
                        CSV template
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* ---- Mode: Add later ---- */}
              {dataSourceMode === "later" && (
                <div className="ev-card-8 text-center py-4 mb-3">
                  <i className="bi bi-clock-history ev-fs-25 text-muted mb-2 d-block" />
                  <p className="ev-reg-p-dark ev-bold-med mb-1">No data source yet — that's fine</p>
                  <p className="ev-small-p-dark text-muted mb-0">
                    You can import a search or upload a CSV anytime from the project's
                    Discovery stage once it's created.
                  </p>
                </div>
              )}

            </div>
          </div>
        </form>
        <DeleteConfirmationModal
          show={showDeleteModal}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          objectName="document"
          itemName={
            pendingDeleteName || `${pendingDeleteIds.length} document(s)`
          }
          bodyContent={
            <div>
              {pendingDeleteIds.length > 1 && (
                <p className="mb-2">
                  This will permanently delete the selected documents.
                </p>
              )}
              {pendingDeleteImpact.linked.length > 0 && (
                <div className="ev-alert-card-warning ev-radius-xsmall p-2">
                  <p className="mb-1">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    <strong>{pendingDeleteImpact.linked.length}</strong> of these
                    document(s) are linked to papers (Study ID{" "}
                    {pendingDeleteImpact.linked.map((d) => d.linked_study_id).join(", ")}).
                    Those papers will be unlinked.
                  </p>
                  {pendingDeleteImpact.withDecisions.length > 0 && (
                    <p className="mb-0 text-danger">
                      Full-text screening decisions (human, AI, conflict and QC) for{" "}
                      <strong>{pendingDeleteImpact.withDecisions.length}</strong> paper(s)
                      will be permanently deleted, and full-text QC will need to be
                      completed again.
                    </p>
                  )}
                </div>
              )}
            </div>
          }
        />

        <DeleteConfirmationModal
          show={showDeleteAbstractsModal}
          onClose={closeDeleteAbstractsModal}
          onConfirm={confirmDeleteAbstractData}
          objectName="Abstract Data"
          itemName={formData.project_name}
          bodyContent={
            <div>
              <p className="mb-0">
                This will permanently delete the abstracts <b>and all screening data</b> in this project:
              </p>
              <ul>
                <li>
                  All {abstractsCount} abstract{abstractsCount === 1 ? "" : "s"} uploaded to this project
                </li>
                <li>
                  Abstract screening — human and AI decisions, conflicts, QC and reviewer allocations
                </li>
                <li>
                  Full-text screening — full-text links, human and AI decisions, conflicts, QC and
                  reviewer allocations
                </li>
                <li>
                  Full-text retrieval history — PubMed Central / Unpaywall attempts and Galaxy order history
                </li>
                <li>Papers added through Add Paper</li>
              </ul>
              <p className="mb-2">
                Not deleted: screening protocol, criteria, tags, framework and AI agent settings, and
                the uploaded PDF files in Data Collection. Screening will have to be run again from the start.
              </p>

              <button
                type="button"
                className="ev-btn-blue-outline-round ev-btn-small mb-3"
                onClick={openAbstractsPreview}
              >
                <i className="bi bi-eye me-1" />
                View uploaded abstracts
              </button>

              <label className="ev-reg-p-dark mb-1 mt-2 d-block">
                Type <b>{formData.project_name}</b> to confirm
              </label>
              <input
                type="text"
                className="formField6"
                value={deleteAbstractsConfirmText}
                onChange={(e) => setDeleteAbstractsConfirmText(e.target.value)}
                onPaste={(e) => e.preventDefault()}
                placeholder="Enter project name"
              />

              <p className="text-danger mb-0 mt-2">This action cannot be undone.</p>
            </div>
          }
        />

        {/* Standalone, wider modal for the uploaded-abstracts grid — kept
            separate from the DeleteConfirmationModal above (which has a fixed
            dialog width shared with other delete flows in this app) so the
            table gets a proper full-width, spreadsheet-like layout instead of
            being squeezed into a narrow confirm dialog. */}
        {abstractsPreviewOpen && (
          <div className="modal d-block bg-dark bg-opacity-50">
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content ev-card-3">
                <div className="row mb-1 modal-header border-0 pb-1">
                  <div className="col-10">
                    <h6 className="ev-message-card3 m-0">
                      Uploaded Abstracts — {formData.project_name}
                    </h6>
                  </div>
                  <div className="col-2 text-end">
                    <button
                      type="button"
                      className="ev-btn-text-only-fill ev-fs-20"
                      onClick={closeAbstractsPreview}
                    >
                      <i className="bi bi-x" />
                    </button>
                  </div>
                </div>

                <div className="modal-body pt-2">
                  <div className="position-relative mb-2 w-100">
                    <input
                      type="text"
                      className="form-control search-input ms-0"
                      placeholder="Search abstracts"
                      value={abstractsPreviewSearch}
                      onChange={(e) => setAbstractsPreviewSearch(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        fetchAbstractsPreview(abstractsPreviewSearch, 1, false)
                      }
                    />
                    <button
                      type="button"
                      className="ev-btn-lightblue-fill-round search-bar-button"
                      onClick={() => fetchAbstractsPreview(abstractsPreviewSearch, 1, false)}
                    >
                      <i className="bi bi-search" />
                    </button>
                  </div>

                  <div className="scrollable-element ev-radius-small vh-50 overflow-auto">
                    {abstractsPreviewLoading ? (
                      <p className="ev-small-p-dark text-muted text-center py-4 mb-0">
                        Loading…
                      </p>
                    ) : abstractsPreviewList.length === 0 ? (
                      <p className="ev-small-p-dark text-muted text-center py-4 mb-0">
                        No abstracts found.
                      </p>
                    ) : (
                      <table className="table-custom2 discovery-table-wide w-100">
                        <thead>
                          <tr>
                            <th className="discovery-col-id discovery-sticky-th">#</th>
                            <th className="discovery-col-title discovery-sticky-th">Title</th>
                            <th className="discovery-col-authors discovery-sticky-th">Authors</th>
                            <th className="discovery-col-year discovery-sticky-th">Year</th>
                          </tr>
                        </thead>
                        <tbody>
                        {abstractsPreviewList.map((a, idx) => (
                          <tr
                            key={a.id}
                            className=""
                          >
                            <td>{idx + 1}</td>
                            <td>{a.title || "(untitled)"}</td>
                            <td>{a.author_names || "Unknown authors"}</td>
                            <td>{a.publication_year || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="ev-small-p-blue">
                      Showing {abstractsPreviewList.length} of {abstractsPreviewTotal}
                    </span>
                    {abstractsPreviewList.length < abstractsPreviewTotal && (
                      <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-btn-small"
                        onClick={() =>
                          fetchAbstractsPreview(
                            abstractsPreviewSearch,
                            abstractsPreviewPage + 1,
                            true,
                          )
                        }
                        disabled={abstractsPreviewLoadingMore}
                      >
                        {abstractsPreviewLoadingMore ? "Loading…" : "View More"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-btn-reg"
                    onClick={closeAbstractsPreview}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}



        {previewOpen && localPreviewUrl ? (
          <div className="modal d-block bg-dark bg-opacity-50">
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content ev-card-3">
                <div className="row mb-2 modal-header border-0">
                  <div className="col-10">
                    <h6 className="ev-message-card3 m-0">
                      {localPreviewName || "Document"}
                    </h6>
                  </div>
                  <div className="col-2 text-end">
                    <button
                      type="button"
                      className="ev-btn-text-only-fill ev-fs-20"
                      onClick={closePreview}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                </div>

                <div className="modal-body p-0 ev-radius-small vh-80">
                  {localPreviewLoading ? (
                    <div className="text-center p-5">
                      <div
                        className="spinner-border ev-med-p-blue"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : null}

                  <object
                    data={localPreviewUrl}
                    type="application/pdf"
                    className={`w-100 h-100 ${localPreviewLoading ? "d-none" : ""}`}
                    onLoad={() => setLocalPreviewLoading(false)}
                  >
                    <iframe
                      title="PDF Preview"
                      src={localPreviewUrl}
                      className="w-100 h-100"
                      onLoad={() => setLocalPreviewLoading(false)}
                    />
                  </object>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <DocumentPreview
            isOpen={previewOpen}
            onClose={closePreview}
            docId={previewDocId}
            documentName={previewDocName}
            bucket_type="input"
          />
        )}
        {isProcessingPopupVisible && (
          <ProcessingPopup message={processingMessage} />
        )}

        {isErrorPopupVisible && (
          <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
        )}

        {isSuccessPopupVisible && (
          <SuccessPopup onClose={closeSuccessPopup} message={popupSuccessMessage} />
        )}

        {/* Team change: assigned reviewers not in the new team will be added on save */}
        <ConfirmationModal
          show={!!pendingTeamChange}
          title="Reviewers will be added to the team"
          message={`These reviewers have papers assigned in this project but are not members of ${pendingTeamChange?.teamName || "the selected team"}. Saving will add them to this team so they keep access to the project and their papers.`}
          confirmButtonText="Add & Save"
          cancelButtonText="Cancel"
          onConfirm={confirmTeamChange}
          onClose={cancelTeamChange}
          bodyContent={
            <ul className="mb-0">
              {(pendingTeamChange?.nonMembers || []).map((r) => (
                <li key={r.user_id} className="ev-reg-p-dark">{r.email}</li>
              ))}
            </ul>
          }
        />
      </div>
    );
  },
);

export default GeneralSettingsForm;